/* global __APP_BUILD_ID__ */
/**
 * useAppUpdate — détection automatique des mises à jour en production.
 *
 * Principe : au build, Vite injecte un identifiant unique dans le bundle
 * (`__APP_BUILD_ID__`) ET l'écrit dans `dist/version.json`. L'app charge une
 * fois `version.json` (servi sans cache par nginx) à intervalle régulier ; si
 * le `buildId` déployé diffère de celui embarqué dans le bundle en cours
 * d'exécution, c'est qu'un nouveau déploiement a eu lieu → on notifie
 * l'utilisateur, qui peut recharger pour obtenir la nouvelle version.
 *
 * État partagé (singleton module) pour que tous les composants voient le même
 * signal `updateAvailable`.
 */
import { ref } from 'vue'

const CURRENT_BUILD_ID = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev'

const updateAvailable = ref(false)
let started = false
let timer = null

async function fetchDeployedBuildId() {
  try {
    const resp = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    })
    if (!resp.ok) return null
    const data = await resp.json().catch(() => null)
    return data?.buildId || null
  } catch {
    return null
  }
}

async function check() {
  if (updateAvailable.value) return
  const deployed = await fetchDeployedBuildId()
  if (deployed && deployed !== CURRENT_BUILD_ID) {
    updateAvailable.value = true
    stopPolling()
  }
}

function stopPolling() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

/** Recharge l'application pour appliquer la nouvelle version. */
function reloadApp() {
  window.location.reload()
}

export function useAppUpdate() {
  /**
   * Démarre la surveillance (idempotent). Inactif hors production (pas de
   * version.json déployé en dev). Vérifie aussi au retour de l'onglet.
   */
  function start({ intervalMs = 60000, initialDelayMs = 15000 } = {}) {
    if (started) return
    started = true
    if (!import.meta.env.PROD) return

    setTimeout(check, initialDelayMs)
    timer = setInterval(check, intervalMs)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check()
    })
    window.addEventListener('focus', check)
  }

  return { updateAvailable, reloadApp, start, check, currentBuildId: CURRENT_BUILD_ID }
}
